import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { LineChart } from '../../components/charts/LineChart.js'
import { Metric } from '../../components/Metric.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount, formatDecimal, formatRate } from '../../lib/format.js'
import { bucketForRange, fromInputDate, PERIOD_OPTIONS, toInputDate, usePeriod } from '../../lib/period.js'
import { usePageTitle } from '../../lib/page-title.js'
import { Button, Heading, Input, Select, Text } from '../../ui/index.js'
import { type TaskListItem, type TaskListPage } from './useTasks.js'

interface PersonRow {
  readonly userId: string
  readonly name: string
  readonly departmentId: string | null
  readonly tasks: number
  readonly onTime: number | null
  readonly firstTimePass: number | null
  readonly avgComplexity: number
  readonly score: number | null
  readonly belowMinimumSample: boolean
}

interface TrendPoint {
  readonly from: string
  readonly to: string
  readonly score: number | null
  readonly onTime: number | null
  readonly firstTimePass: number | null
}

interface TrendResponse {
  readonly points: readonly TrendPoint[]
}

type TaskStatusFilter = 'all' | 'ASSIGNED' | 'IN_PROGRESS' | 'SUBMITTED' | 'RETURNED' | 'APPROVED'

interface PersonTaskRow extends TaskListItem {
  readonly firstSubmittedAt?: string | null
  readonly returnCount?: number
}

function taskQueryPath(userId: string, from: string, to: string, status: TaskStatusFilter): string {
  const params = new URLSearchParams({ assigneeId: userId, from, to })
  if (status !== 'all') params.set('statuses', status)
  return `/api/tasks?${params.toString()}`
}

function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso))
}

function usePersonRows(userId: string | null, from: string, to: string) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['person-analytics-people', userId, from, to],
    enabled: userId !== null,
    retry: false,
    queryFn: () => api.get<PersonRow[]>(`/api/analytics/people?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  })
}

function useTrendRows(userId: string | null, from: string, to: string, bucket: 'week' | 'month') {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['person-analytics-trend', userId, from, to, bucket],
    enabled: userId !== null,
    retry: false,
    queryFn: () =>
      api.get<TrendResponse>(
        `/api/analytics/trend?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}&assigneeId=${encodeURIComponent(userId!)}`,
      ),
  })
}

function useTaskRows(userId: string | null, from: string, to: string, status: TaskStatusFilter) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['person-analytics-tasks', userId, from, to, status],
    enabled: userId !== null,
    retry: false,
    queryFn: () => api.get<TaskListPage>(taskQueryPath(userId!, from, to, status)),
  })
}

function PersonSummaryPanel({ userId, from, to }: { readonly userId: string | null; readonly from: string; readonly to: string }) {
  const peopleQuery = usePersonRows(userId, from, to)
  const person = useMemo(
    () => peopleQuery.data?.find((row) => row.userId === userId) ?? null,
    [peopleQuery.data, userId],
  )

  if (peopleQuery.isPending) return <LoadingState label="Loading person summary" />
  if (peopleQuery.isError) return <ErrorState error={peopleQuery.error} onRetry={() => void peopleQuery.refetch()} />
  if (person === null) return <ErrorState error={new Error('Person not found')} />

  const efficiency = person.score === null ? '—' : formatCount(person.score)

  return (
    <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)', minWidth: 0 }}>
      <div className="metric-grid">
        <Metric label="Efficiency" value={efficiency} unit="/100" footnote="from the selected period" />
        <Metric label="Tasks" value={formatCount(person.tasks)} footnote="closed tasks in scope" />
        <Metric label="On-time" value={formatRate(person.onTime)} footnote="complexity-weighted" />
        <Metric label="First-time pass" value={formatRate(person.firstTimePass)} footnote="complexity-weighted" />
      </div>

      <div style={{ marginTop: 'var(--o-space-4)', display: 'grid', gap: 'var(--o-space-2)' }}>
        <div className="card-label">Score breakdown</div>
        <Heading type="h3" style={{ margin: 0 }}>0.6 × onTime + 0.4 × firstTimePass</Heading>
        <Text color="secondary">
          On-time: {person.onTime === null ? 'Not enough data' : formatRate(person.onTime)}.
          First-time pass: {person.firstTimePass === null ? 'Not enough data' : formatRate(person.firstTimePass)}.
          Weighted by the closed-task complexity in this period.
        </Text>
      </div>
    </section>
  )
}

function PersonTrendPanel({
  userId,
  from,
  to,
  bucket,
}: {
  readonly userId: string | null
  readonly from: string
  readonly to: string
  readonly bucket: 'week' | 'month'
}) {
  const trendQuery = useTrendRows(userId, from, to, bucket)
  if (trendQuery.isPending) return <LoadingState label="Loading trend" />
  if (trendQuery.isError) return <ErrorState error={trendQuery.error} onRetry={() => void trendQuery.refetch()} />
  const points = trendQuery.data?.points ?? []
  if (points.length === 0) return <EmptyState title="No trend data" hint="Pick a period with closed work to see the line chart." />

  return (
    <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
        <div>
          <div className="card-label">Trend</div>
          <Heading type="h3" style={{ margin: 0 }}>Efficiency over time</Heading>
        </div>
        <Text color="secondary">This panel updates independently from the summary and task list.</Text>
      </div>
      <LineChart
        width={680}
        height={220}
        yMax={100}
        xLabels={points.map((point) => point.from.slice(5, 10))}
        series={[
          { label: 'Score', data: points.map((point) => point.score), color: 'var(--brand)', fill: true, dots: true },
        ]}
      />
    </section>
  )
}

function PersonTasksPanel({
  userId,
  from,
  to,
}: {
  readonly userId: string | null
  readonly from: string
  readonly to: string
}) {
  const [statusFilter, setStatusFilter] = useState<TaskStatusFilter>('all')
  useEffect(() => {
    setStatusFilter('all')
  }, [userId])

  const tasksQuery = useTaskRows(userId, from, to, statusFilter)
  const rows = useMemo(
    () => (tasksQuery.data?.items ?? []) as readonly PersonTaskRow[],
    [tasksQuery.data],
  )

  if (tasksQuery.isPending) return <LoadingState label="Loading task history" />
  if (tasksQuery.isError) return <ErrorState error={tasksQuery.error} onRetry={() => void tasksQuery.refetch()} />

  return (
    <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--o-space-3)', marginBottom: 'var(--o-space-3)' }}>
        <div>
          <div className="card-label">Task history</div>
          <Heading type="h3" style={{ margin: 0 }}>Filterable tasks</Heading>
        </div>
        <div style={{ minWidth: 180 }}>
          <Select aria-label="Status" value={statusFilter} onChange={(value) => setStatusFilter(value as TaskStatusFilter)}>
            <option value="all">All</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="RETURNED">Returned</option>
            <option value="APPROVED">Approved</option>
          </Select>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No matching tasks" hint="Adjust the status filter to see more of this person's work." />
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Reference</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Title</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Status</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Category</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Complexity</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Assigned</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Deadline</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>First submitted</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Closed</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>On-time</th>
                <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Returns</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const firstSubmitted = row.firstSubmittedAt ?? null
                const onTime = firstSubmitted === null ? null : Date.parse(firstSubmitted) <= Date.parse(row.effectiveDeadlineAt)
                return (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)', fontFamily: 'var(--font-mono)' }}>{row.reference}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)', fontWeight: 500 }}>{row.title}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{row.status}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{row.category}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{row.complexity}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatDate(row.assignedAt)}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatDate(row.deadlineAt)}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatDate(firstSubmitted)}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatDate(row.closedAt)}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{onTime === null ? '—' : onTime ? 'On time' : 'Late'}</td>
                    <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{row.returnCount ?? 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export function PersonAnalytics() {
  usePageTitle('Person')
  const navigate = useNavigate()
  const { api } = useAuth()
  const { userId } = useParams<{ userId: string }>()
  const resolvedUserId = userId ?? null
  const {
    period,
    setPeriod,
    from,
    to,
    bucket,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    isValidRange,
  } = usePeriod('quarter')
  const peopleQuery = usePersonRows(resolvedUserId, from, to)
  const person = useMemo(
    () => peopleQuery.data?.find((row) => row.userId === resolvedUserId) ?? null,
    [peopleQuery.data, resolvedUserId],
  )
  const title = person?.name ?? 'Analytics'
  const invalidCustomRange = period === 'custom' && !isValidRange
  const exportQuery = useMemo(() => {
    if (resolvedUserId === null) return null
    return new URLSearchParams({ from, to, userId: resolvedUserId })
  }, [from, resolvedUserId, to])

  return (
    <>
      <Topbar
        title={title}
        eyebrow="Analytics"
        actions={
          exportQuery === null ? null : (
            <div style={{ display: 'flex', gap: 'var(--o-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                kind="secondary"
                size="small"
                onClick={async () => {
                  const blob = await api.getBlob(`/api/reports/person.csv?${exportQuery.toString()}`)
                  const url = URL.createObjectURL(blob)
                  const anchor = document.createElement('a')
                  anchor.href = url
                  anchor.download = `ovrsee-${resolvedUserId}.csv`
                  anchor.click()
                  URL.revokeObjectURL(url)
                }}
              >
                Export CSV
              </Button>
              <Button
                kind="secondary"
                size="small"
                onClick={() => {
                  navigate(`/reports/print?${exportQuery.toString()}`)
                }}
              >
                Export PDF
              </Button>
            </div>
          )
        }
      />
      <div className="page">
        <div className="page-section" style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
          <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)', minWidth: 0, display: 'grid', gap: 'var(--o-space-3)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--o-space-2)' }}>
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={period === option.value}
                  onClick={() => setPeriod(option.value)}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '6px 12px',
                    background: period === option.value ? 'var(--brand-soft)' : 'transparent',
                    color: period === option.value ? 'var(--mauve-700)' : 'var(--fg-muted)',
                    fontSize: 12.5,
                    fontWeight: 500,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {period === 'custom' ? (
              <div style={{ display: 'grid', gap: 'var(--o-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span className="card-label">From</span>
                  <Input
                    type="date"
                    value={customFrom === '' ? '' : toInputDate(customFrom)}
                    onChange={(value) => setCustomFrom(value === '' ? '' : fromInputDate(value))}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span className="card-label">To</span>
                  <Input
                    type="date"
                    value={customTo === '' ? '' : toInputDate(customTo)}
                    onChange={(value) => setCustomTo(value === '' ? '' : fromInputDate(value))}
                  />
                </label>
              </div>
            ) : null}

            {invalidCustomRange ? (
              <Text color="secondary">Choose an end date after the start date to update the panels.</Text>
            ) : null}
          </section>

          <PersonSummaryPanel userId={resolvedUserId} from={from} to={to} />
          <PersonTrendPanel userId={resolvedUserId} from={from} to={to} bucket={bucketForRange(from, to)} />
          <PersonTasksPanel userId={resolvedUserId} from={from} to={to} />
        </div>
      </div>
    </>
  )
}
